import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useHelpPanel } from "../../../hooks/useHelpPanel";
import { helpArticlePaths } from "../../../content/help";
import { buildMarkdownComponents, helpUrlTransform } from "./markdownComponents";

const MIN_WIDTH = 500;
const MAX_WIDTH = 700;
const DEFAULT_WIDTH = 500;

export const HelpPanel: React.FC = () => {
    const { helpPanel, openHelp, closeHelp, goBack, canGoBack } = useHelpPanel();

    const [content, setContent] = useState("");
    const [width, setWidth] = useState(DEFAULT_WIDTH);

    useEffect(() => {
        if (!helpPanel.open) return;
        const path = helpArticlePaths[helpPanel.article];
        if (!path) return;
        fetch(path)
            .then((res) => res.text())
            .then(setContent)
            .catch(() => setContent("Failed to load help content."));
    }, [helpPanel.open, helpPanel.article]);

    // Renderer map is shared with the Material Guide page; see markdownComponents.tsx.
    const mdComponents: Components = useMemo(() => buildMarkdownComponents(openHelp), [openHelp]);

    const dragging = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(DEFAULT_WIDTH);

    const onMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        dragging.current = true;
        startX.current = e.clientX;
        startWidth.current = width;
    }, [width]);

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            if (!dragging.current) return;
            const delta = startX.current - e.clientX;
            const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth.current + delta));
            setWidth(next);
        };

        const onMouseUp = () => {
            dragging.current = false;
        };

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, []);

    return (
        <div
            className={`shrink-0 overflow-hidden print:hidden ${
                dragging.current ? "" : "transition-all duration-300 ease-in-out"
            } ${helpPanel.open ? "border-l border-gray-200 dark:border-stone-700" : "w-0"}`}
            style={helpPanel.open ? { width } : undefined}
        >
            <div className="relative h-full flex flex-col bg-surface" style={{ width }}>
                {/* Drag handle on the left edge */}
                <div
                    onMouseDown={onMouseDown}
                    className="absolute left-0 top-0 bottom-0 w-3 cursor-col-resize z-10 flex items-center justify-end group pr-0.5"
                >
                    <div className="h-[200px] w-[3px] rounded-full bg-gray-300 dark:bg-stone-600 group-hover:bg-emerald-500 transition-colors" />
                </div>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-stone-700">
                    <div className="flex items-center gap-2">
                        {canGoBack && (
                            <button
                                onClick={goBack}
                                className="flex items-center gap-1 rounded-md p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                            >
                                <ArrowLeftIcon className="h-4 w-4" />
                                <span className="text-sm">Back</span>
                            </button>
                        )}
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            Help
                        </h2>
                    </div>
                    <button
                        onClick={closeHelp}
                        className="rounded-md p-1 pr-6 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto pl-5 pr-12 py-4 pb-24">
                    <ReactMarkdown components={mdComponents} urlTransform={helpUrlTransform}>{content}</ReactMarkdown>
                </div>
            </div>
        </div>
    );
};
